import { useSiteDashboard } from '@/hooks/useSiteDashboard';
import { Receipt } from 'lucide-react';
import { useDashboardRecentSessions } from '@/hooks/useDashboardRecentSessions';
import { formatCurrency, formatRelativeTime } from './_lib';
import styles from '../DashboardPage.module.css';

/**
 * Recent Sessions widget. Last 10 historical sessions in scope,
 * sorted started_at desc. Click-through to /sessions/:id.
 *
 * Empty-state copy reads scope-aware: "No sessions at your
 * assigned locations yet" for third_party_admin, plain "No
 * sessions yet" for tenant-internal staff. Without that
 * distinction a Building Admin scoped to a quiet location would
 * see "No sessions yet" and assume the platform is broken.
 */
export default function RecentSessionsWidget({ onNavigate }: { onNavigate?: (to: string) => void }) {
  const isSite = useSiteDashboard();
  const Row = isSite ? 'div' : 'a';
  const q = useDashboardRecentSessions();
  const data = q.data;

  return (
    <div className={styles.chartBody}>
      <div className={styles.chartHeader}>
        <div className={styles.chartIcon}><Receipt size={14} /></div>
        <p className={styles.chartTitle}>Recent sessions</p>
      </div>

      {q.isLoading && !data && (
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartDesc}>Loading…</p>
        </div>
      )}

      {data && data.rows.length === 0 && (
        <div className={styles.anomEmpty}>
          <span className={styles.anomEmptyIcon}><Receipt size={18} /></span>
          <p>
            {data.is_scoped
              ? 'No sessions at your assigned locations yet.'
              : 'No sessions yet.'}
          </p>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className={styles.recentSessionList}>
          {data.rows.map((row) => (
            <Row
              key={row.session_id}
              href={isSite ? undefined : `/sessions/${row.session_id}`}
              onClick={(e) => {
                if (!isSite && onNavigate) {
                  e.preventDefault();
                  onNavigate(`/sessions/${row.session_id}`);
                }
              }}
              className={styles.recentSessionRow}
              style={isSite ? { cursor: 'default' } : undefined}
            >
              <div className={styles.recentSessionBody}>
                <span className={styles.recentSessionTitleLine}>
                  {row.charger_identity}
                  {row.location_name ? ` · ${row.location_name}` : ''}
                </span>
                <span className={styles.recentSessionMetaLine}>
                  {!isSite && `${row.driver_name} · `}{formatRelativeTime(row.started_at)} · {row.energy_kwh.toFixed(1)} kWh
                </span>
              </div>
              <span className={styles.recentSessionAmount}>
                {formatCurrency(row.total_cost_cents / 100)}
              </span>
              <span
                className={`${styles.recentSessionStatusPill} ${recentSessionStatusClass(row.status)}`}
              >
                {recentSessionStatusLabel(row.status)}
              </span>
            </Row>
          ))}
        </div>
      )}
    </div>
  );
}

export function recentSessionStatusClass(status: string): string {
  if (status === 'active') return styles.recentSessionStatusPillActive ?? '';
  if (status === 'completed') return styles.recentSessionStatusPillCompleted ?? '';
  if (status === 'failed') return styles.recentSessionStatusPillFailed ?? '';
  return styles.recentSessionStatusPillStopped ?? '';
}

export function recentSessionStatusLabel(status: string): string {
  if (status === 'active') return 'Charging';
  if (status === 'completed') return 'Completed';
  if (status === 'failed') return 'Failed';
  if (status === 'stopped_by_user') return 'Stopped';
  if (status === 'stopped_by_system') return 'System stop';
  return status;
}
